// Email Express 2.0 — creation workspace (Task #2701).
//
// Additive, master-admin-only. Loads existing brand products/personas/
// narrative-angles/offers via the SAME endpoints and selection semantics as
// other Express tools (see broll-express.tsx) — no V1 Email Express
// component is imported or modified. Every structured field is mirrored
// into sessionStorage so a refresh or back-navigation doesn't lose partial
// input; the complete context is only sent to the server on Generate.
import { useEffect, useMemo, useState } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2, Mail, Package, UserCircle, Compass, Sparkles, Tag, Wand2,
  ArrowRight, Clock3, AlertCircle, Link2, CheckCircle2,
} from "lucide-react";
import type {
  Persona,
  Product,
  Offer,
  NarrativeAngle,
  EmailExpress2CampaignType,
  EmailExpress2EmailType,
  EmailExpress2Design,
} from "@shared/schema";
import {
  emailExpress2HasInitialContext,
} from "@shared/schema";
import { PickerSelect } from "@/components/picker-select";
import { groupAnglesByFamily } from "@/lib/angle-groups";
import { selectablePersonas } from "@shared/persona-select";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { KlaviyoConnectionBadge } from "@/components/email-express/klaviyo-connection";
import { emailExpress2ErrorMessage } from "@/lib/email-express-2-errors";

type MagicPatternsDesignSystem = {
  id: string;
  name: string;
  isReserved: boolean;
  isActive: boolean;
};

type DesignSystemsResponse = {
  designSystems: MagicPatternsDesignSystem[];
  selectedDesignSystemId: string | null;
};

const DEFAULT_DESIGN_SYSTEM_VALUE = "__reserved_default__";

const CAMPAIGN_TYPE_LABELS: Record<EmailExpress2CampaignType, string> = {
  education: "Education",
  sale: "Sale",
  product_highlight: "Product highlight",
  review_led: "Review-led",
  announcement: "Announcement",
  welcome: "Welcome",
  cart_abandonment: "Cart abandonment",
  browse_abandonment: "Browse abandonment",
  retention: "Retention",
};

const EMAIL_TYPE_LABELS: Record<EmailExpress2EmailType, string> = {
  promotional: "Promotional",
  newsletter: "Newsletter",
  announcement: "Announcement",
  abandoned_cart: "Abandoned cart",
  welcome: "Welcome",
  post_purchase: "Post-purchase",
  re_engagement: "Re-engagement",
};

const CAMPAIGN_ONLY_CAMPAIGN_TYPES: EmailExpress2CampaignType[] = [
  "education",
  "sale",
  "product_highlight",
  "review_led",
  "announcement",
];

const CAMPAIGN_ONLY_EMAIL_TYPES: EmailExpress2EmailType[] = [
  "promotional",
  "newsletter",
  "announcement",
];

interface DraftState {
  productId: string;
  personaId: string;
  angleMode: "saved" | "custom";
  narrativeAngleId: string;
  customAngle: string;
  offerId: string;
  campaignType: EmailExpress2CampaignType;
  emailType: EmailExpress2EmailType;
  briefText: string;
  ctaUrl: string;
}

const EMPTY_DRAFT: DraftState = {
  productId: "",
  personaId: "",
  angleMode: "saved",
  narrativeAngleId: "",
  customAngle: "",
  offerId: "",
  campaignType: "product_highlight",
  emailType: "promotional",
  briefText: "",
  ctaUrl: "",
};

function draftKey(brandId: string): string {
  return `email-express-2:create-draft:${brandId}`;
}

function loadDraft(brandId: string): DraftState {
  try {
    const raw = sessionStorage.getItem(draftKey(brandId));
    if (!raw) return { ...EMPTY_DRAFT };
    const parsed = JSON.parse(raw);
    return { ...EMPTY_DRAFT, ...parsed };
  } catch {
    return { ...EMPTY_DRAFT };
  }
}

export default function EmailExpress2CreatePage() {
  const { brandId } = useParams<{ brandId: string }>();
  const { toast } = useToast();
  const { user } = useAuth();
  const [, navigate] = useLocation();

  const [draft, setDraft] = useState<DraftState>(() => loadDraft(brandId));

  // Persist every field change so a refresh/back-nav restores it.
  useEffect(() => {
    try {
      sessionStorage.setItem(draftKey(brandId), JSON.stringify(draft));
    } catch {
      // sessionStorage unavailable (private mode, quota) — non-fatal.
    }
  }, [draft, brandId]);

  const set = <K extends keyof DraftState>(key: K, value: DraftState[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const { data: products = [], isLoading: productsLoading } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`],
    enabled: !!brandId,
  });
  const { data: personasRaw = [], isLoading: personasLoading } = useQuery<Persona[]>({
    queryKey: [`/api/brands/${brandId}/personas`],
    enabled: !!brandId,
  });
  const personas = useMemo(() => selectablePersonas(personasRaw), [personasRaw]);
  const { data: angles = [] } = useQuery<NarrativeAngle[]>({
    queryKey: [`/api/brands/${brandId}/narrative-angles`],
    enabled: !!brandId,
  });
  const { data: offers = [], isLoading: offersLoading } = useQuery<Offer[]>({
    queryKey: [`/api/brands/${brandId}/offers`],
    enabled: !!brandId,
  });
  const { data: recentDesigns = [], isLoading: recentDesignsLoading } = useQuery<EmailExpress2Design[]>({
    queryKey: [`/api/brands/${brandId}/email-express-2/designs`],
    enabled: !!brandId,
    refetchInterval: (query) =>
      (query.state.data ?? []).some((design) => design.status === "generating") ? 4000 : false,
  });
  const { data: designSystemsData, isLoading: designSystemsLoading, error: designSystemsError } = useQuery<DesignSystemsResponse>({
    queryKey: [`/api/brands/${brandId}/email-express-2/design-systems`],
    enabled: !!brandId && user?.isMaster === true,
    retry: false,
  });
  const accessibleDesignSystems = (designSystemsData?.designSystems ?? []).filter(
    (system) => !(system.isReserved && system.name.trim().toLowerCase() === "base"),
  );
  const selectedDesignSystemValue = accessibleDesignSystems.some(
    (system) => system.id === designSystemsData?.selectedDesignSystemId,
  )
    ? designSystemsData!.selectedDesignSystemId!
    : DEFAULT_DESIGN_SYSTEM_VALUE;

  const designSystemMutation = useMutation({
    mutationFn: async (value: string) => {
      const designSystemId = value === DEFAULT_DESIGN_SYSTEM_VALUE ? null : value;
      const res = await apiRequest(
        "PATCH",
        `/api/brands/${brandId}/email-express-2/design-system`,
        { designSystemId },
      );
      return (await res.json()) as { ok: true; designSystemId: string | null };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/email-express-2/design-systems`],
      });
      toast({ title: "Design system saved." });
    },
    onError: (error: any) => {
      toast({
        title: "Couldn't save design system",
        description: emailExpress2ErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const availableAngles = useMemo(
    () =>
      angles.filter((a) => {
        if (draft.personaId && a.personaId !== draft.personaId) return false;
        if (draft.productId && a.productId && a.productId !== draft.productId) return false;
        return true;
      }),
    [angles, draft.personaId, draft.productId],
  );
  const angleGroups = useMemo(
    () =>
      groupAnglesByFamily(availableAngles).map((g) => ({
        label: g.label,
        options: g.angles.map((a) => ({ value: a.id, label: a.name, description: a.summary ?? null })),
      })),
    [availableAngles],
  );

  useEffect(() => {
    if (draft.narrativeAngleId && !availableAngles.some((a) => a.id === draft.narrativeAngleId)) {
      set("narrativeAngleId", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableAngles]);

  const selectedProduct = products.find((p) => p.id === draft.productId);

  const createMutation = useMutation({
    mutationFn: async () => {
      const name = `${selectedProduct?.name || "Brand email"} — ${EMAIL_TYPE_LABELS[draft.emailType]}`.slice(0, 255);
      const body = {
        name,
        productId: draft.productId || null,
        personaId: draft.personaId || null,
        narrativeAngleId: draft.angleMode === "saved" ? draft.narrativeAngleId || null : null,
        customAngle: draft.angleMode === "custom" ? draft.customAngle.trim() || null : null,
        offerId: draft.offerId || null,
        campaignType: draft.campaignType,
        emailType: draft.emailType,
        briefText: draft.briefText.trim(),
        referenceImageUrl: null,
        ctaUrl: draft.ctaUrl.trim() || null,
      };
      const res = await apiRequest("POST", `/api/brands/${brandId}/email-express-2/designs`, body);
      return (await res.json()) as { id: string; status: string };
    },
    onSuccess: (result) => {
      try {
        sessionStorage.removeItem(draftKey(brandId));
      } catch {
        // ignore
      }
      navigate(`/brands/${brandId}/email-express-2/${result.id}`);
    },
    onError: (e: any) => {
      toast({ title: "Couldn't start generation", description: emailExpress2ErrorMessage(e), variant: "destructive" });
    },
  });

  const hasGenerationContext = emailExpress2HasInitialContext({
    productId: draft.productId,
    personaId: draft.personaId,
    narrativeAngleId: draft.angleMode === "saved" ? draft.narrativeAngleId : null,
    customAngle: draft.angleMode === "custom" ? draft.customAngle : null,
    briefText: draft.briefText,
  });
  const ctaUrlValid = !draft.ctaUrl.trim() || (() => {
    try {
      const url = new URL(draft.ctaUrl.trim());
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  })();
  const canGenerate = hasGenerationContext && ctaUrlValid && !createMutation.isPending;

  return (
    <div className="mx-auto max-w-6xl space-y-7 px-4 py-8 sm:px-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 text-primary ring-1 ring-primary/20 shadow-sm">
            <Mail className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-3xl font-semibold tracking-tight" data-testid="text-page-title">
              Email Express 2.0
            </h1>
            <p className="text-sm text-muted-foreground">
              Generate an editable email design — plain HTML, no builder lock-in.
            </p>
          </div>
        </div>
        {brandId && user?.isMaster === true && <KlaviyoConnectionBadge brandId={brandId} />}
      </div>

      {user?.isMaster === true && (
        <Card data-testid="card-magic-patterns-design-system">
          <CardContent className="space-y-2 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium">Brand design system</p>
                <p className="text-xs text-muted-foreground">
                  Select an available design system for this brand, or use the reserved default.
                </p>
              </div>
              <Select
                value={selectedDesignSystemValue}
                onValueChange={(value) => designSystemMutation.mutate(value)}
                disabled={designSystemsLoading || designSystemMutation.isPending}
              >
                <SelectTrigger className="w-full sm:w-[280px]" data-testid="select-magic-patterns-design-system">
                  <SelectValue placeholder={designSystemsLoading ? "Loading…" : "Choose design system"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={DEFAULT_DESIGN_SYSTEM_VALUE}>
                    Reserved default (Base)
                  </SelectItem>
                  {accessibleDesignSystems.map((system) => (
                    <SelectItem key={system.id} value={system.id}>
                      {system.name}
                      {system.isReserved ? " (built-in)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {designSystemsError && (
              <p className="text-xs text-destructive" data-testid="text-design-systems-error">
                {emailExpress2ErrorMessage(designSystemsError, "Couldn't load design systems.")}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="relative overflow-hidden border-border/60 bg-gradient-to-b from-card to-muted/30 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(0,0,0,0.12)]">
        <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
        <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-primary/[0.06] blur-3xl" />
        <CardContent className="relative space-y-7 p-6 sm:p-8">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 text-primary ring-1 ring-primary/20">
              <Wand2 className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-base font-semibold tracking-tight">Set your inputs</h2>
              <p className="mt-0.5 text-xs text-muted-foreground">Add the context that should shape this email. A brief or one strategic input is enough to begin.</p>
            </div>
          </div>

          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">1</span>
              <div><h3 className="text-sm font-semibold">Audience and product</h3><p className="text-xs text-muted-foreground">Ground the design in what you sell and who it is for.</p></div>
            </div>
          <div className="grid gap-5 md:grid-cols-2">
            <PickerSelect
              icon={<Package className="h-4 w-4" />}
              label="Product (optional)"
              placeholder={productsLoading ? "Loading…" : "Choose product"}
              value={draft.productId}
              onChange={(v) => set("productId", v)}
              options={[...products]
                .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
                .map((p) => {
                  const media = (p as { mediaJson?: string[] | null }).mediaJson;
                  const first = Array.isArray(media) && media.length > 0 ? media[0] : null;
                  return { value: p.id, label: p.name, imageUrl: first };
                })}
              testId="select-product"
              searchable
              optional
              disabled={productsLoading || createMutation.isPending}
              onClear={draft.productId ? () => set("productId", "") : undefined}
            />
            <PickerSelect
              icon={<UserCircle className="h-4 w-4" />}
              label="Persona / audience (optional)"
              placeholder={personasLoading ? "Loading…" : "Choose persona"}
              value={draft.personaId}
              onChange={(v) => set("personaId", v)}
              options={personas.map((p) => ({ value: p.id, label: p.name, imageUrl: p.imageUrl }))}
              testId="select-persona"
              searchable
              optional
              disabled={personasLoading || createMutation.isPending}
              onClear={draft.personaId ? () => set("personaId", "") : undefined}
            />
          </div>
          </section>

          <section className="space-y-4 border-t border-border/50 pt-6">
            <div className="flex items-center gap-3">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">2</span>
              <div><h3 className="text-sm font-semibold">Strategy</h3><p className="text-xs text-muted-foreground">Choose the campaign angle, offer, and message type.</p></div>
            </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Compass className="h-4 w-4 text-muted-foreground" /> Narrative angle (optional)
              </label>
              <div className="flex items-center gap-1 p-0.5 rounded-full border border-border/70 bg-background/60 shadow-sm" data-testid="toggle-angle-mode">
                <Button
                  type="button"
                  size="sm"
                  variant={draft.angleMode === "saved" ? "default" : "ghost"}
                  className="h-7 px-3 text-[11px] rounded-full"
                  onClick={() => set("angleMode", "saved")}
                  disabled={createMutation.isPending}
                  data-testid="toggle-angle-saved"
                >
                  Saved angle
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={draft.angleMode === "custom" ? "default" : "ghost"}
                  className="h-7 px-3 text-[11px] rounded-full"
                  onClick={() => set("angleMode", "custom")}
                  disabled={createMutation.isPending}
                  data-testid="toggle-angle-custom"
                >
                  Custom angle
                </Button>
              </div>
            </div>
            {draft.angleMode === "saved" ? (
              <PickerSelect
                icon={<Compass className="h-4 w-4" />}
                label="Saved angle (optional)"
                placeholder={availableAngles.length === 0 ? "No saved angles yet" : "Choose angle"}
                value={draft.narrativeAngleId}
                onChange={(v) => set("narrativeAngleId", v)}
                groups={angleGroups}
                testId="select-angle"
                searchable
                wrapDescription
                optional
                disabled={availableAngles.length === 0 || createMutation.isPending}
                onClear={draft.narrativeAngleId ? () => set("narrativeAngleId", "") : undefined}
              />
            ) : (
              <Textarea
                value={draft.customAngle}
                onChange={(e) => set("customAngle", e.target.value)}
                rows={3}
                placeholder="Describe the angle driving this email — e.g. 'summer heat makes your usual routine feel like too much work'"
                disabled={createMutation.isPending}
                data-testid="input-custom-angle"
              />
            )}
          </div>

          <PickerSelect
            icon={<Sparkles className="h-4 w-4" />}
            label="Offer (optional)"
            placeholder={offersLoading ? "Loading…" : offers.length === 0 ? "No offers yet" : "Choose offer"}
            value={draft.offerId}
            onChange={(v) => set("offerId", v)}
            options={offers.map((o) => ({ value: o.id, label: o.name, description: (o as any).summary || null }))}
            testId="select-offer"
            searchable
            optional
            disabled={offersLoading || offers.length === 0 || createMutation.isPending}
            onClear={draft.offerId ? () => set("offerId", "") : undefined}
          />

          <div className="grid gap-5 md:grid-cols-2">
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Tag className="h-4 w-4 text-muted-foreground" /> Campaign goal
              </label>
              <Select
                value={draft.campaignType}
                onValueChange={(v) => set("campaignType", v as EmailExpress2CampaignType)}
                disabled={createMutation.isPending}
              >
                <SelectTrigger data-testid="select-campaign-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CAMPAIGN_ONLY_CAMPAIGN_TYPES.map((ct) => (
                    <SelectItem key={ct} value={ct}>{CAMPAIGN_TYPE_LABELS[ct]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Mail className="h-4 w-4 text-muted-foreground" /> Email format
              </label>
              <Select
                value={draft.emailType}
                onValueChange={(v) => set("emailType", v as EmailExpress2EmailType)}
                disabled={createMutation.isPending}
              >
                <SelectTrigger data-testid="select-email-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CAMPAIGN_ONLY_EMAIL_TYPES.map((et) => (
                    <SelectItem key={et} value={et}>{EMAIL_TYPE_LABELS[et]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          </section>

          <section className="space-y-4 border-t border-border/50 pt-6">
            <div className="flex items-center gap-3">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">3</span>
              <div><h3 className="text-sm font-semibold">Creative direction</h3><p className="text-xs text-muted-foreground">Describe the campaign goal and CTA destination.</p></div>
            </div>
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-sm font-medium">
              <Wand2 className="h-4 w-4 text-muted-foreground" /> Brief
            </label>
            <Textarea
              value={draft.briefText}
              onChange={(e) => set("briefText", e.target.value)}
              rows={4}
              placeholder="What should this email say and do? e.g. 'Announce the new colorway, lead with the hero shot, one CTA to shop now.'"
              disabled={createMutation.isPending}
              data-testid="input-brief"
            />
          </div>

          <div className="space-y-1.5">
            <label htmlFor="ee2-cta-url" className="flex items-center gap-2 text-sm font-medium">
              <Link2 className="h-4 w-4 text-muted-foreground" /> CTA destination (optional)
            </label>
            <Input
              id="ee2-cta-url"
              type="url"
              value={draft.ctaUrl}
              onChange={(e) => set("ctaUrl", e.target.value)}
              placeholder="https://example.com/shop"
              disabled={createMutation.isPending}
              data-testid="input-cta-url"
            />
            <p className="text-xs text-muted-foreground">Used for button links in the generated email. When blank, the selected product URL is used when available.</p>
            {!ctaUrlValid && <p className="text-xs text-destructive" data-testid="cta-url-error">Enter a complete http:// or https:// URL.</p>}
          </div>

          </section>

          <div className="flex items-center justify-end gap-3 border-t border-border/50 pt-5">
            {!hasGenerationContext && (
              <p className="text-sm text-destructive" data-testid="generate-context-error">
                Add a brief, or select a product/persona/angle
              </p>
            )}
            <Button size="lg" className="gap-2" onClick={() => createMutation.mutate()} disabled={!canGenerate} data-testid="button-generate">
              {createMutation.isPending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Starting…</>
              ) : (
                <><Sparkles className="mr-2 h-4 w-4" /> Generate</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="card-recent-designs" className="overflow-hidden">
        <CardContent className="p-0">
          <div className="flex items-center justify-between gap-3 border-b px-6 py-5">
            <div>
              <h2 className="font-semibold">Recent designs</h2>
              <p className="text-sm text-muted-foreground">Open a previous design or check generation progress.</p>
            </div>
            <Clock3 className="h-5 w-5 text-muted-foreground" />
          </div>
          {recentDesignsLoading ? (
            <div className="flex items-center justify-center gap-2 px-6 py-12 text-sm text-muted-foreground" data-testid="recent-designs-loading">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading designs…
            </div>
          ) : recentDesigns.length === 0 ? (
            <div className="px-6 py-12 text-center" data-testid="recent-designs-empty">
              <Mail className="mx-auto mb-3 h-8 w-8 text-muted-foreground/50" />
              <p className="text-sm font-medium">No designs yet</p>
              <p className="mt-1 text-sm text-muted-foreground">Your generated designs will appear here.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead className="bg-muted/40 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  <tr><th className="px-6 py-3">Design</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Updated</th><th className="px-6 py-3 text-right">Action</th></tr>
                </thead>
                <tbody className="divide-y">
                  {recentDesigns.map((design) => (
                    <tr key={design.id} className="transition-colors hover:bg-muted/30">
                      <td className="px-6 py-4"><p className="max-w-xs truncate font-medium">{design.name}</p><p className="mt-0.5 text-xs text-muted-foreground">{CAMPAIGN_TYPE_LABELS[design.campaignType as EmailExpress2CampaignType] ?? design.campaignType}</p></td>
                      <td className="px-4 py-4">{EMAIL_TYPE_LABELS[design.emailType as EmailExpress2EmailType] ?? design.emailType}</td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${design.status === "ready" ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" : design.status === "failed" ? "bg-destructive/10 text-destructive" : "bg-amber-500/10 text-amber-700 dark:text-amber-400"}`}>
                          {design.status === "generating" && <Loader2 className="h-3 w-3 animate-spin" />}
                          {design.status === "failed" && <AlertCircle className="h-3 w-3" />}
                          {design.status === "ready" && <CheckCircle2 className="h-3 w-3" />}
                          {design.status === "ready" ? "Ready" : design.status === "failed" ? "Failed" : "Generating"}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-muted-foreground">{(design.updatedAt ?? design.createdAt) ? new Date((design.updatedAt ?? design.createdAt)!).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—"}</td>
                      <td className="px-6 py-4 text-right"><Button type="button" size="sm" variant="ghost" onClick={() => navigate(`/brands/${brandId}/email-express-2/${design.id}`)} data-testid={`button-open-design-${design.id}`}>Open <ArrowRight className="ml-1.5 h-4 w-4" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
